import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { offlinePost } from '../services/api';
import { trackEvent } from '../services/analytics';
import { fetchInsightProgress } from '../utils/insightProgress';
import { getProgressMessage } from '../utils/insightReadiness';

const KNOWN_INGREDIENTS = [
  // High-FODMAP
  'garlic', 'onion', 'wheat', 'honey', 'lentils', 'chickpeas', 'beans', 'mushrooms',
  'cauliflower', 'artichoke', 'asparagus', 'apple', 'pear', 'watermelon', 'mango',
  'peach', 'plum', 'cherry', 'nectarine', 'apricot', 'shallot', 'leek', 'scallion',
  'rye', 'barley', 'agave',
  // Dairy
  'milk', 'cheese', 'cream', 'yogurt', 'butter', 'ice cream', 'whey', 'ghee',
  'mozzarella', 'cheddar', 'parmesan', 'feta', 'ricotta', 'brie', 'kefir', 'sour cream',
  // Nightshades
  'tomato', 'bell pepper', 'eggplant', 'potato', 'jalapeño', 'chili', 'paprika', 'cayenne',
  // Cruciferous
  'broccoli', 'cabbage', 'brussels sprouts', 'kale', 'bok choy', 'arugula', 'radish',
  // Caffeine
  'coffee', 'espresso', 'tea', 'green tea', 'matcha', 'chocolate', 'cocoa', 'energy drink',
  // Alcohol
  'beer', 'wine', 'vodka', 'whiskey', 'rum', 'gin', 'tequila', 'champagne', 'cider',
  // Sweeteners
  'sorbitol', 'aspartame', 'sucralose', 'stevia', 'erythritol',
  // High-fat/fried
  'fried chicken', 'french fries', 'pizza', 'bacon', 'sausage', 'burger', 'nachos', 'wings',
  // Common foods (non-trigger)
  'rice', 'white rice', 'brown rice', 'chicken', 'salmon', 'tuna', 'shrimp', 'beef', 'pork',
  'lamb', 'tofu', 'egg', 'eggs', 'bread', 'pasta', 'noodles', 'oats', 'oatmeal', 'quinoa',
  'corn', 'carrot', 'celery', 'cucumber', 'lettuce', 'spinach', 'zucchini', 'avocado',
  'banana', 'blueberries', 'strawberries', 'grapes', 'orange', 'lemon', 'lime',
  'peanut butter', 'almonds', 'walnuts', 'olive oil', 'coconut oil', 'soy sauce',
  'salt', 'pepper', 'ginger', 'turmeric', 'cinnamon', 'cumin', 'oregano', 'basil',
  'mayo', 'mustard', 'ketchup', 'hot sauce', 'salsa', 'hummus',
].sort();



function getNowDatetimeLocal() {
  const d = new Date();
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0') + 'T' +
    String(d.getHours()).padStart(2, '0') + ':' +
    String(d.getMinutes()).padStart(2, '0');
}


function LogMeal() {
  const navigate = useNavigate();
  const location = useLocation();
  // Get image passed from Home page (legacy support)
  const capturedImage = location.state?.capturedImage;

  // Tab state
  const [activeTab, setActiveTab] = useState('photo');

  // Shared date/time state
  const [manualTimestamp, setManualTimestamp] = useState(getNowDatetimeLocal());

  // Custom timestamp state (for photo tab)
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [customDatetime, setCustomDatetime] = useState(getNowDatetimeLocal());

  // Photo state
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(capturedImage || null);
  const [preview, setPreview] = useState(capturedImage ? URL.createObjectURL(capturedImage) : null);

  // Manual state
  const [description, setDescription] = useState('');

  // Shared state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Clarification state
  const [needsClarification, setNeedsClarification] = useState(false);
  const [clarificationOptions, setClarificationOptions] = useState([]);
  const [pendingMealId, setPendingMealId] = useState(null);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Concealed food ingredient confirmation state
  const [ingredientSelections, setIngredientSelections] = useState({});
  const [confirmedFoods, setConfirmedFoods] = useState(new Set());
  // Editable ingredients state
  const [editableFoods, setEditableFoods] = useState(null); // null = not editing yet
  const [newIngredientText, setNewIngredientText] = useState('');
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [addingToFoodIndex, setAddingToFoodIndex] = useState(null);
  const [foodsEdited, setFoodsEdited] = useState(false);
  const [photoHint, setPhotoHint] = useState('');
  // Auto-submit when image is passed from Home (legacy)
  useEffect(() => {
    if (capturedImage && !result && !loading) {
      handlePhotoSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showProgressToast = () => {
    fetchInsightProgress().then(({ mealsCount, poopsCount, daysCovered, hasAnalyzed }) => {
      const msg = getProgressMessage('meal', { mealsCount, poopsCount, daysCovered }, hasAnalyzed);
      if (msg) {
        setToastMessage(msg);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3500);
      }
    }).catch(() => {});
  };

  const handleCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
      setResult(null);
      setNeedsClarification(false);
    }
  };

  const handlePhotoSubmit = async () => {
    if (!image) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', image);
      if (photoHint.trim()) formData.append('hint', photoHint.trim());
      const timestamp = useCustomTime ? new Date(customDatetime).toISOString() : new Date().toISOString();
      formData.append('timestamp', timestamp);
      formData.append('mealType', (() => { const h = new Date().getHours(); return h < 11 ? 'morning' : h < 16 ? 'lunch' : 'dinner'; })());

      {
        const response = await offlinePost('/meals', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.offline) {
          setResult({
            offline: true,
            foods: [{ name: 'Meal saved offline', category: 'Pending sync' }],
            notes: 'This meal will be analyzed when you\'re back online'
          });
          trackEvent('meal_logged');
          showProgressToast();
          return;
        }

        const hasConcealed = (response.data.foods || []).some(f => f.isConcealed);
        if (hasConcealed) {
          initIngredientSelections(response.data.foods);
          setPendingMealId(response.data.id);
        }

        const lowConfidenceFoods = response.data.foods?.filter(f => f.confidence && f.confidence < 0.7);
        if (lowConfidenceFoods?.length > 0 && response.data.clarificationOptions) {
          setNeedsClarification(true);
          setClarificationOptions(response.data.clarificationOptions);
          setPendingMealId(response.data.id);
          setResult(response.data);
        } else {
          setResult(response.data);
        }
        trackEvent('meal_logged');
        showProgressToast();
      }
    } catch (error) {
      console.error('Failed to upload meal:', error);
      alert('Failed to analyze meal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!description.trim()) {
      alert('Please describe what you ate');
      return;
    }

    setLoading(true);
    try {
      const timestamp = new Date(manualTimestamp).toISOString();

      {
        const response = await offlinePost('/meals/manual', {
          description: description.trim(),
          timestamp,
          mealType: (() => { const h = new Date(manualTimestamp).getHours(); return h < 11 ? 'morning' : h < 16 ? 'lunch' : 'dinner'; })()
        });

        if (response.data.offline) {
          setResult({
            offline: true,
            foods: [{ name: description.trim(), ingredients: [] }],
            notes: 'Saved offline — will be analyzed when connected'
          });
        } else {
          setResult(response.data);
        }
        trackEvent('meal_logged');
        showProgressToast();
      }
    } catch (error) {
      console.error('Failed to log meal:', error);
      alert('Failed to log meal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClarification = async (foodIndex, selectedOption) => {
    try {
      await api.put(`/meals/${pendingMealId}/clarify`, {
        foodIndex,
        correctedName: selectedOption
      });

      const updatedFoods = [...result.foods];
      updatedFoods[foodIndex].name = selectedOption;
      updatedFoods[foodIndex].confidence = 1.0;
      setResult({ ...result, foods: updatedFoods });

      const remainingOptions = clarificationOptions.filter((_, i) => i !== foodIndex);
      if (remainingOptions.length === 0) {
        setNeedsClarification(false);
        setClarificationOptions([]);
      } else {
        setClarificationOptions(remainingOptions);
      }
    } catch (error) {
      console.error('Failed to update:', error);
    }
  };

  // Concealed food helpers
  const initIngredientSelections = (foods) => {
    const selections = {};
    (foods || []).forEach((food, idx) => {
      if (food.isConcealed) {
        const confirmed = Array.isArray(food.confirmedIngredients) ? food.confirmedIngredients : [];
        selections[idx] = new Set(confirmed);
      }
    });
    setIngredientSelections(selections);
    setConfirmedFoods(new Set());
  };

  const toggleIngredient = (foodIndex, ingredient) => {
    setIngredientSelections(prev => {
      const current = new Set(prev[foodIndex] || []);
      if (current.has(ingredient)) {
        current.delete(ingredient);
      } else {
        current.add(ingredient);
      }
      return { ...prev, [foodIndex]: current };
    });
  };

  const handleConfirmIngredients = async (foodIndex) => {
    const selectedIngredients = Array.from(ingredientSelections[foodIndex] || []);

    const updatedFoods = [...result.foods];
    updatedFoods[foodIndex] = {
      ...updatedFoods[foodIndex],
      ingredients: selectedIngredients
    };
    setResult({ ...result, foods: updatedFoods });

    setConfirmedFoods(prev => new Set([...prev, foodIndex]));

    if (pendingMealId) {
      try {
        await api.put(`/meals/${pendingMealId}/ingredients`, {
          foods: [{ foodIndex, ingredients: selectedIngredients }]
        });
      } catch (error) {
        console.error('Failed to update ingredients:', error);
      }
    }
  };

  // Initialize editable foods when result arrives
  useEffect(() => {
    if (result && result.foods && !editableFoods) {
      setEditableFoods(result.foods.map(f => ({
        name: f.name,
        ingredients: Array.isArray(f.ingredients) ? [...f.ingredients] :
          (typeof f.ingredients === 'string' ? f.ingredients.split(', ').filter(Boolean) : [])
      })));
    }
  }, [result, editableFoods]);

  const removeIngredient = (foodIdx, ingIdx) => {
    setEditableFoods(prev => {
      const updated = [...prev];
      updated[foodIdx] = {
        ...updated[foodIdx],
        ingredients: updated[foodIdx].ingredients.filter((_, i) => i !== ingIdx)
      };
      return updated;
    });
    setFoodsEdited(true);
  };

  const addIngredient = (foodIdx) => {
    const text = newIngredientText.trim();
    if (!text) return;
    setEditableFoods(prev => {
      const updated = [...prev];
      updated[foodIdx] = {
        ...updated[foodIdx],
        ingredients: [...updated[foodIdx].ingredients, text]
      };
      return updated;
    });
    setNewIngredientText('');
    setAddingToFoodIndex(null);
    setFoodsEdited(true);
  };

  const removeFoodItem = (foodIdx) => {
    setEditableFoods(prev => prev.filter((_, i) => i !== foodIdx));
    setFoodsEdited(true);
  };

  const saveEditedFoods = async () => {
    if (!foodsEdited || !editableFoods) return;
    // For authenticated users with a meal ID, PATCH the foods
    const mealId = result?.id || pendingMealId;
    if (mealId && !String(mealId).startsWith('offline-')) {
      try {
        await api.patch(`/meals/${mealId}/foods`, { foods: editableFoods });
      } catch (err) {
        console.error('Failed to save edited foods:', err);
      }
    }
    setFoodsEdited(false);
  };

  const handleDone = async () => {
    await saveEditedFoods();
    navigate('/');
  };

  const handleAddAnother = async () => {
    await saveEditedFoods();
    setActiveTab('photo');
    setImage(null);
    setPreview(null);
    setDescription('');
    setResult(null);
    setLoading(false);
    setNeedsClarification(false);
    setClarificationOptions([]);
    setPendingMealId(null);
    setIngredientSelections({});
    setConfirmedFoods(new Set());
    setEditableFoods(null);
    setFoodsEdited(false);
    setAddingToFoodIndex(null);
    setNewIngredientText('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRetake = () => {
    setImage(null);
    setPreview(null);
    setResult(null);
    setNeedsClarification(false);
    setClarificationOptions([]);
    setIngredientSelections({});
    setConfirmedFoods(new Set());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRetakeAfterLog = async () => {
    // Delete the saved meal before resetting
    try {
      if (result?.id) {
        await api.delete(`/meals/${result.id}`);
      }
    } catch (err) {
      console.error('Failed to delete meal for retake:', err);
    }
    handleRetake();
  };

  // Result display — shared between photo and manual
  const renderResult = () => {
    if (!result) return null;

    const concealedIndices = (result.foods || [])
      .map((f, i) => f.isConcealed ? i : null)
      .filter(i => i !== null);
    const allConcealedConfirmed = concealedIndices.length === 0 ||
      concealedIndices.every(i => confirmedFoods.has(i));

    return (
      <>
        <div className="card mt-2">
          <h3 style={{ margin: '0 0 16px 0' }}>Meal Logged!</h3>
          {result.foods?.map((food, index) => {
            const isConcealed = food.isConcealed && !confirmedFoods.has(index);
            const allIngredients = [
              ...(Array.isArray(food.confirmedIngredients) ? food.confirmedIngredients : []),
              ...(Array.isArray(food.possibleIngredients) ? food.possibleIngredients : [])
            ];
            const selections = ingredientSelections[index];

            return (
              <div key={index} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: index < result.foods.length - 1 ? '1px solid #E8D9C8' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#4A2E1F' }}>{food.name}</p>
                    {editableFoods && result.foods.length > 1 && (
                      <button
                        onClick={() => removeFoodItem(index)}
                        style={{
                          background: 'none', border: 'none', color: '#B8564A', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600', padding: '0', marginBottom: '8px', fontFamily: 'inherit'
                        }}
                      >
                        remove
                      </button>
                    )}
                  </div>
                  {food.confidence && food.confidence < 0.7 && !food.isConcealed && (
                    <span style={{
                      background: '#F5ECDB',
                      color: '#B87A2E',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      Uncertain
                    </span>
                  )}
                  {food.isConcealed && !confirmedFoods.has(index) && (
                    <span style={{
                      background: '#E5EDE5',
                      color: '#5A8A60',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      Confirm ingredients
                    </span>
                  )}
                </div>

                {/* Ingredient chips for concealed foods */}
                {isConcealed && selections && (
                  <>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#7A5A44' }}>
                      We can't see inside — tap to select what's in it:
                    </p>
                    <div className="symptom-chips">
                      {allIngredients.map(ingredient => (
                        <button
                          key={ingredient}
                          className={`symptom-chip${selections.has(ingredient) ? ' active' : ''}`}
                          onClick={() => toggleIngredient(index, ingredient)}
                        >
                          {ingredient}
                        </button>
                      ))}
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: '12px', height: '44px', fontSize: '15px', borderRadius: '14px', width: '100%' }}
                      onClick={() => handleConfirmIngredients(index)}
                    >
                      Confirm Ingredients
                    </button>
                  </>
                )}

                {/* Confirmed concealed food — show final ingredients */}
                {food.isConcealed && confirmedFoods.has(index) && food.ingredients && (
                  <p style={{ margin: '0', fontSize: '14px', color: '#7A5A44' }}>
                    {Array.isArray(food.ingredients) ? food.ingredients.join(', ') : food.ingredients}
                  </p>
                )}

                {/* Non-concealed food — editable ingredient chips */}
                {!food.isConcealed && editableFoods && editableFoods[index] && (
                  <div style={{ marginTop: '4px' }}>
                    <div className="symptom-chips" style={{ marginBottom: '4px' }}>
                      {editableFoods[index].ingredients.map((ing, ingIdx) => (
                        <span
                          key={ingIdx}
                          className="symptom-chip active"
                          style={{
                            background: '#F5EDE3',
                            borderColor: '#E8D9C8',
                            color: '#4A2E1F',
                            paddingRight: '6px',
                          }}
                        >
                          {ing}
                          <button
                            onClick={() => removeIngredient(index, ingIdx)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#7A5A44',
                              cursor: 'pointer',
                              padding: '0 0 0 4px',
                              fontSize: '14px',
                              fontWeight: '700',
                              lineHeight: 1,
                              fontFamily: 'inherit',
                            }}
                            aria-label={`Remove ${ing}`}
                          >
                            x
                          </button>
                        </span>
                      ))}
                      {addingToFoodIndex === index ? (
                        <span className="symptom-chip" style={{ padding: '2px 4px', position: 'relative' }}>
                          <input
                            type="text"
                            value={newIngredientText}
                            onChange={e => {
                              const val = e.target.value;
                              setNewIngredientText(val);
                              setSelectedSuggestionIndex(-1);
                              if (val.trim().length >= 2) {
                                const lower = val.trim().toLowerCase();
                                const existing = (result.foods[index]?.ingredients || []).map(i => i.toLowerCase());
                                setAutocompleteSuggestions(
                                  KNOWN_INGREDIENTS.filter(k => k.includes(lower) && !existing.includes(k)).slice(0, 5)
                                );
                              } else {
                                setAutocompleteSuggestions([]);
                              }
                            }}
                            onKeyDown={e => {
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setSelectedSuggestionIndex(i => Math.min(i + 1, autocompleteSuggestions.length - 1));
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setSelectedSuggestionIndex(i => Math.max(i - 1, -1));
                              } else if (e.key === 'Enter') {
                                if (selectedSuggestionIndex >= 0 && autocompleteSuggestions[selectedSuggestionIndex]) {
                                  setNewIngredientText(autocompleteSuggestions[selectedSuggestionIndex]);
                                  setAutocompleteSuggestions([]);
                                  setSelectedSuggestionIndex(-1);
                                } else {
                                  addIngredient(index);
                                  setAutocompleteSuggestions([]);
                                }
                              } else if (e.key === 'Escape') {
                                setAddingToFoodIndex(null);
                                setNewIngredientText('');
                                setAutocompleteSuggestions([]);
                              }
                            }}
                            onBlur={() => setTimeout(() => setAutocompleteSuggestions([]), 150)}
                            autoFocus
                            placeholder="ingredient"
                            autoComplete="off"
                            style={{
                              border: 'none',
                              outline: 'none',
                              background: 'transparent',
                              fontFamily: 'inherit',
                              fontSize: '13px',
                              width: '100px',
                              color: '#4A2E1F',
                            }}
                          />
                          <button
                            onClick={() => { addIngredient(index); setAutocompleteSuggestions([]); }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#7E8B47',
                              cursor: 'pointer',
                              fontWeight: '700',
                              fontSize: '14px',
                              padding: '0 2px',
                              fontFamily: 'inherit',
                            }}
                          >
                            +
                          </button>
                          {autocompleteSuggestions.length > 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              background: 'white',
                              border: '1px solid #E8D9C8',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              zIndex: 10,
                              marginTop: '4px',
                              overflow: 'hidden',
                              minWidth: '150px',
                            }}>
                              {autocompleteSuggestions.map((s, i) => (
                                <button
                                  key={s}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    // Auto-add the ingredient immediately
                                    setEditableFoods(prev => {
                                      const updated = [...prev];
                                      if (updated[index] && !updated[index].ingredients.includes(s.toLowerCase())) {
                                        updated[index] = { ...updated[index], ingredients: [...updated[index].ingredients, s.toLowerCase()] };
                                      }
                                      return updated;
                                    });
                                    setNewIngredientText('');
                                    setAutocompleteSuggestions([]);
                                    setSelectedSuggestionIndex(-1);
                                    setFoodsEdited(true);
                                  }}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: 'none',
                                    background: i === selectedSuggestionIndex ? '#f0edd4' : 'transparent',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: '#4A2E1F',
                                    fontFamily: 'inherit',
                                    borderBottom: i < autocompleteSuggestions.length - 1 ? '1px solid #f0edd4' : 'none',
                                  }}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          )}
                        </span>
                      ) : (
                        <button
                          className="symptom-chip"
                          onClick={() => { setAddingToFoodIndex(index); setNewIngredientText(''); }}
                          style={{ borderStyle: 'dashed', color: '#7A5A44', opacity: 0.7 }}
                        >
                          + add
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Fallback: non-concealed food without editableFoods yet */}
                {!food.isConcealed && !editableFoods && food.ingredients && (
                  <p style={{ margin: '0', fontSize: '14px', color: '#7A5A44' }}>
                    {Array.isArray(food.ingredients) ? food.ingredients.join(', ') : food.ingredients}
                  </p>
                )}

                {food.restaurant && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#7A5A44' }}>
                    📍 {food.restaurant}
                  </p>
                )}

                {/* Clarification options for uncertain items */}
                {needsClarification && food.confidence && food.confidence < 0.7 && food.alternatives && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{ fontSize: '12px', color: '#7A5A44', margin: '0 0 8px 0' }}>
                      Did you mean:
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {food.alternatives.map((alt, altIdx) => (
                        <button
                          key={altIdx}
                          className="btn btn-outline"
                          style={{ padding: '4px 12px', fontSize: '14px' }}
                          onClick={() => handleClarification(index, alt)}
                        >
                          {alt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button
            className="btn"
            style={{
              flex: '0 0 auto',
              background: 'none',
              border: '1px solid #D8CEBC',
              color: '#7A5A44',
              fontSize: '14px',
              height: '48px',
              borderRadius: '14px',
              padding: '0 14px',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
            onClick={handleRetakeAfterLog}
          >
            Retake
          </button>
          <button
            className="btn btn-outline"
            style={{ flex: 1 }}
            onClick={handleAddAnother}
          >
            + Add Another
          </button>
          <button
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={handleDone}
            disabled={!allConcealedConfirmed}
          >
            {foodsEdited ? 'Save & Done' : 'Done'}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="page">
      {showToast && (
        <div
          className="success-flash"
          style={{ animationDuration: '3.5s', cursor: toastMessage.includes('Insights') ? 'pointer' : undefined }}
          onClick={toastMessage.includes('Insights') ? () => navigate('/insights') : undefined}
        >
          {toastMessage}
        </div>
      )}
      <div className="page-header">
        <h1 className="page-title">Log Meal</h1>
      </div>

      <div className="container" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <input
          type="file"
          id="meal-photo-input"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />

        {/* Tab bar */}
        {!result && (
          <div className="meal-tabs">
            <button
              className={`meal-tab${activeTab === 'photo' ? ' active' : ''}`}
              onClick={() => setActiveTab('photo')}
            >
              Take a Pic
            </button>
            <button
              className={`meal-tab${activeTab === 'manual' ? ' active' : ''}`}
              onClick={() => setActiveTab('manual')}
            >
              Log Manually
            </button>
          </div>
        )}

        {/* Custom timestamp for photo tab */}
        {!result && activeTab === 'photo' && (
          <div style={{ marginBottom: '12px' }}>
            {!useCustomTime ? (
              <button
                onClick={() => setUseCustomTime(true)}
                style={{
                  background: '#f7f5e8',
                  border: '1px solid #d4cfb8',
                  borderRadius: '10px',
                  color: '#4A2E1F',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '10px 16px',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7E8B47" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                Log for a different time
              </button>
            ) : (
              <div className="card" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '700', color: '#4A2E1F' }}>When did this happen?</label>
                  <button
                    onClick={() => { setUseCustomTime(false); setCustomDatetime(getNowDatetimeLocal()); }}
                    style={{ background: 'none', border: 'none', color: '#7A5A44', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Use current time
                  </button>
                </div>
                <input
                  type="datetime-local"
                  className="input"
                  value={customDatetime}
                  onChange={(e) => setCustomDatetime(e.target.value)}
                  max={getNowDatetimeLocal()}
                />
              </div>
            )}
          </div>
        )}

        {/* Timestamp picker — only for manual entry */}
        {!result && activeTab === 'manual' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', color: '#4A2E1F', fontSize: '14px' }}>When did you eat?</label>
            <input
              type="datetime-local"
              className="input"
              value={manualTimestamp}
              onChange={(e) => setManualTimestamp(e.target.value)}
              max={getNowDatetimeLocal()}
            />
          </div>
        )}

        {/* Take a Pic tab */}
        {activeTab === 'photo' && !preview && !result && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: '80px', gap: '16px' }}>
            <label
              htmlFor="meal-photo-input"
              className="btn btn-primary"
              style={{ cursor: 'pointer' }}
            >
              Tap to Take Photo
            </label>
          </div>
        )}

        {/* Photo preview + analysis */}
        {activeTab === 'photo' && preview && (
          <>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <img
                src={preview}
                alt="Meal preview"
                style={{
                  width: '100%',
                  maxHeight: '45vh',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>

            {!result && !loading && (
              <div style={{ marginTop: '12px' }}>
                <input
                  type="text"
                  value={photoHint}
                  onChange={(e) => setPhotoHint(e.target.value)}
                  placeholder="What is this? (optional, e.g. dal, pad thai)"
                  style={{
                    width: '100%', padding: '10px 14px', border: '1px solid #E8D9C8', borderRadius: '12px',
                    fontSize: '14px', fontFamily: 'inherit', color: '#4A2E1F', background: 'white',
                    marginBottom: '12px', boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  position: 'sticky',
                  bottom: '80px',
                  background: '#FFF3E3',
                  padding: '12px 0',
                  zIndex: 10
                }}>
                  <button className="btn btn-outline" onClick={handleRetake}>
                    Retake
                  </button>
                  <button className="btn btn-primary" onClick={handlePhotoSubmit}>
                    Analyze
                  </button>
                </div>
              </div>
            )}

            {loading && (
              <div className="analyzing-container">
                <div className="analyzing-spinner"></div>
                <p className="analyzing-text">Analyzing your meal...</p>
              </div>
            )}

            {renderResult()}
          </>
        )}

        {/* Manual entry */}
        {activeTab === 'manual' && (
          <>
            {!result ? (
              <>
                <div className="card" style={{ padding: '16px' }}>
                  <textarea
                    className="input"
                    placeholder="Describe what you ate (e.g., chicken tikka masala with garlic naan and a side of rice)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    style={{ resize: 'vertical', minHeight: '100px' }}
                  />
                </div>

                <button
                  className="btn btn-primary mt-2"
                  onClick={handleManualSubmit}
                  disabled={loading || !description.trim()}
                  style={{ width: '100%' }}
                >
                  {loading ? 'Analyzing...' : 'Analyze'}
                </button>

                {loading && (
                  <div className="analyzing-container">
                    <div className="analyzing-spinner"></div>
                    <p className="analyzing-text">Analyzing your meal...</p>
                  </div>
                )}

                <button
                  className="btn btn-outline mt-2"
                  onClick={() => navigate('/')}
                  style={{ width: '100%' }}
                >
                  Cancel
                </button>
              </>
            ) : (
              renderResult()
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default LogMeal;
